import React, { useEffect, useState } from "react";
import Lead from "../LeadManagerComponents/Lead";
import LeadDetailsDrawer from "../LeadManagerComponents/LeadDetailsDrawer";
import axios from "../../api/axiosInstance";
import {
  Typography,
  Pagination,
  Snackbar,
  Slide,
  Alert,
  Box,
  Modal,
  Icon,
  Stack,
  Button,
  Fab,
} from "@mui/material";
import LeadsSkeleton from "../LeadsSkeleton";
import { updateLeadStatus } from "../../utils/updateLeadStatus";
import AddIcon from "@mui/icons-material/Add";
import TotalDelete from "../LeadManagerComponents/TotalDelete";

const TransitionUp = (props) => <Slide {...props} direction="up" />;
const LeadsManager = () => {
  const [leads, setLeads] = useState([]); // or fetch from backend
  const [selectedLead, setSelectedLead] = useState(null);
  const [page, setPage] = useState(1);
  const [showMessage, setShowMessage] = useState("");
  const [markedLeads, setMarkedLeads] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedLead && selectedLead.id > 0 && !selectedLead?.communications) {
      fetchLeadCommunications(selectedLead.id);
    }
  }, [selectedLead]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get("/list", {
          params: { page },
        });

        setLeads(res.data.data);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [page]);

  const fetchLeadCommunications = async (leadId) => {
    try {
      const response = await axios.get(`/communications/${leadId}`);
      const communications = response.data.communications;

      // Update the lead in the leads state
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === leadId ? { ...lead, communications } : lead
        )
      );
      // Update the selected lead with new communications
      setSelectedLead((prev) => ({
        ...prev,
        communications,
      }));
    } catch (error) {
      console.error("Failed to fetch communications", error);
    }
  };

  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
  };

  const handleSingleDelete = async (leadId) => {
    try {
      const res = await axios.post("/bulk-delete", {
        ids: [leadId],
      });
      setShowMessage(res.data.message);
      // Remove deleted leads from local state
      setLeads((prevLeads) => prevLeads.filter((lead) => lead.id !== leadId));
    } catch (e) {
      setError(e);
    }
  };

  const handleDeleteLeads = async () => {
    setConfirmDelete(false);
    try {
      const res = await axios.post("/bulk-delete", {
        ids: markedLeads,
      });
      setShowMessage(res.data.message);
      // Remove deleted leads from local state
      setLeads((prevLeads) =>
        prevLeads.filter((lead) => !markedLeads.includes(lead.id))
      );
    } catch (e) {
      setError(e);
    }

    setMarkedLeads([]);
  };

  const handleAddCommunication = async (newComment) => {
    try {
      // 1. Send to backend
      const response = await axios.post(`/communication/${selectedLead.id}`, {
        text: newComment,
      });

      const savedNote = {
        id: response.data.id, // assuming backend returns inserted ID
        text: newComment,
        created_at: new Date().toISOString(), // ideally use real value from backend
      };

      // 2. Update the leads array in state
      setLeads((prevLeads) =>
        prevLeads.map((lead) => {
          if (lead.id === selectedLead.id) {
            const updatedComms = [savedNote, ...(lead.communications || [])];
            return { ...lead, communications: updatedComms };
          }
          return lead;
        })
      );

      setSelectedLead((prev) => ({
        ...prev,
        communications: [savedNote, ...(prev.communications || [])],
      }));

      // 3. Optionally show success message
      setShowMessage(response.data.message);
    } catch (err) {
      console.error("Failed to save note:", err);
      alert("Error saving communication");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    setLeads((prevLeads) =>
      prevLeads.map((lead) =>
        lead.id === id ? { ...lead, status: newStatus } : lead
      )
    );

    // 👇 Update the selected lead too
    if (selectedLead?.id === id) {
      setSelectedLead((prev) => ({
        ...prev,
        status: newStatus,
      }));
    }

    try {
      const result = await updateLeadStatus(id, newStatus);
      setShowMessage(result.message);
      // Optionally update local state
    } catch (err) {
      alert("Failed to update status");
    }
  };
  const handleLeadUpdate = async (updatedLead) => {
    const isNew = updatedLead.leadId === -1;
    setSelectedLead(null);
    //Map the lead to correct format:
    const formattedLead = {
      id: updatedLead.leadId, // must keep the ID for identification
      name: updatedLead.leadName,
      email: updatedLead.leadEmail,
      phone: updatedLead.leadPhone,
      company: updatedLead.leadCompany,
      landing_page: updatedLead.landingPage,
      comments: updatedLead.leadInitialComments,
      status: updatedLead.leadStatus,
      strength: updatedLead.leadPotential,
    };
    if (isNew) {
      try {
        // Insert new lead
        const response = await axios.post(`/addLead`, formattedLead);
        const newLead = response.data.lead;
        // Add new lead to top of list
        setLeads((prevLeads) => [newLead, ...prevLeads]);

        setShowMessage(response.data.message);
      } catch (error) {
        console.error("Failed to add lead:", error);
      }
    } else {
      // 1. Optimistically update the state
      setLeads((prevLeads) =>
        prevLeads.map((lead) =>
          lead.id === formattedLead.id ? { ...lead, ...formattedLead } : lead
        )
      );

      try {
        const response = await axios.post(`/updateLead`, formattedLead);
        setShowMessage(response.data.message);
      } catch (error) {
        console.error("Failed to update lead:", error);
        // Optional: Rollback state if needed
      }
    }
  };

  const handleOnMark = (leadId) => {
    setMarkedLeads((prev) => {
      if (prev.includes(leadId)) {
        // remove it
        return prev.filter((id) => id !== leadId);
      } else {
        // add it
        return [...prev, leadId];
      }
    });
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Leads
      </Typography>
      <Box my={4}>
        <Fab
          color="primary"
          aria-label="add"
          onClick={() =>
            setSelectedLead({
              id: -1,
              name: "",
              status: "New",
              strength: 0,
              phone: "",
              email: "",
              leadCompany: "",
              comments: "",
              landing_page: "",
            })
          }
        >
          <AddIcon />
        </Fab>
      </Box>
      {leads && (
        <Box
          display={"flex"}
          justifyContent={markedLeads.length > 0 ? "space-between" : "flex-end"}
          alignItems={"center"}
        >
          {markedLeads.length > 0 && (
            <TotalDelete
              onDelete={() => setConfirmDelete(true)}
              total={markedLeads.length}
            />
          )}
          <Pagination
            count={leads.length}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", mb: 2 }}
          />
        </Box>
      )}

      {leads.map((lead) => (
        <Lead
          key={lead.id}
          lead={lead}
          onClick={() => handleSelectLead(lead)}
          onStatusChange={handleStatusChange}
          onMark={handleOnMark}
          isChecked={markedLeads.includes(lead.id)}
          onDeleteClick={() => handleSingleDelete(lead.id)}
        />
      ))}
      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby="confirm-delete-title"
        aria-describedby="confirm-delete-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="confirm-delete-title" variant="h6" gutterBottom>
            Confirm Delete
          </Typography>
          <Typography id="confirm-delete-description" mb={3}>
            Are you sure you want to delete {markedLeads.length} leads?
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteLeads}
              variant="outlined"
              color="error"
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Modal>
      {leads && (
        <Pagination
          count={leads.length}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          sx={{ mt: 4, display: "flex", justifyContent: "center" }}
        />
      )}
      {isLoading && <LeadsSkeleton />}
      <LeadDetailsDrawer
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onStatusChange={handleStatusChange}
        onLeadUpdate={handleLeadUpdate}
        onAddCommunication={handleAddCommunication}
      />
      {showMessage !== "" && (
        <Snackbar
          open={showMessage !== ""}
          autoHideDuration={6000}
          onClose={() => setShowMessage("")}
          slots={{ transition: TransitionUp }}
        >
          <Alert
            onClose={() => setShowMessage("")}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {showMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default LeadsManager;
